/**
 * Utilidades para exportar reportes de trazabilidad a PDF
 * @module utils/pdfExport
 */

import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { TraceResult, type Cable } from '@/types/fibers'
import { AnyNode, isCliente } from '@/types/nodes'
import { describeFiberIdForUi } from '@/utils/fiberIdDisplay'
import { cableLabelShort } from '@/utils/cableDisplayLabel'
import type { IntegratedBudgetResult } from '@/services/opticalBudgetService'

interface ExportOptions {
  trace: TraceResult
  clientNode?: AnyNode
  nodeMap: Map<string, AnyNode>
  cableMap?: Map<string, Cable>
  /** Mismo cálculo que el panel (fibra + empalmes + conectores + splitters NAP) */
  integratedBudget?: IntegratedBudgetResult | null
}

const greenBanner: [number, number, number] = [16, 185, 129]
const yellowBanner: [number, number, number] = [234, 179, 8]
const redBanner: [number, number, number] = [239, 68, 68]

export function exportTraceToPDF(options: ExportOptions) {
  const { trace, clientNode, nodeMap, cableMap, integratedBudget: budget } = options
  const cablesList = cableMap ? Array.from(cableMap.values()) : []

  // Crear documento
  const doc = new jsPDF()

  // Configuración de colores
  const primaryColor: [number, number, number] = [59, 130, 246] // Azul
  const dangerColor: [number, number, number] = [239, 68, 68] // Rojo
  const grayColor: [number, number, number] = [107, 114, 128] // Gris

  const legacyLoss = calculateTotalLoss(trace)
  const totalLoss = budget ? budget.perdidaTotal : legacyLoss
  const statusLegacy = getStatus(legacyLoss)
  const statusColor: [number, number, number] = budget
    ? budget.esViable
      ? budget.margenRestante >= 3
        ? greenBanner
        : yellowBanner
      : redBanner
    : getStatusColor(legacyLoss)
  const bannerTitle = budget
    ? budget.esViable
      ? budget.margenRestante >= 3
        ? 'VIABLE'
        : 'VIABLE (MARGEN BAJO)'
      : 'NO VIABLE'
    : statusLegacy.toUpperCase()
  const showCriticalFooter = budget ? !budget.esViable : statusLegacy === 'crítico'

  let yPosition = 20

  // ============================================================================
  // ENCABEZADO
  // ============================================================================

  // Logo y título (simulado)
  doc.setFillColor(...primaryColor)
  doc.rect(15, 15, 8, 8, 'F')

  doc.setFontSize(24)
  doc.setTextColor(30, 41, 59) // Gris oscuro
  doc.text('NetEasyFTTX', 28, 21)

  doc.setFontSize(10)
  doc.setTextColor(...grayColor)
  doc.text('Sistema de Gestión FTTH', 28, 26)

  // Fecha y hora
  const now = new Date()
  const dateStr = now.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
  const timeStr = now.toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit'
  })

  doc.setFontSize(9)
  doc.setTextColor(...grayColor)
  doc.text(`Fecha: ${dateStr}`, 150, 21)
  doc.text(`Hora: ${timeStr}`, 150, 26)

  yPosition = 40

  // ============================================================================
  // TÍTULO DEL REPORTE
  // ============================================================================

  doc.setFontSize(18)
  doc.setTextColor(...primaryColor)
  doc.text('Reporte de Trazabilidad de Fibra', 15, yPosition)

  yPosition += 3
  doc.setDrawColor(...grayColor)
  doc.setLineWidth(0.5)
  doc.line(15, yPosition, 195, yPosition)

  yPosition += 10

  // ============================================================================
  // INFORMACIÓN GENERAL
  // ============================================================================

  doc.setFontSize(12)
  doc.setTextColor(30, 41, 59)
  doc.text('Información General', 15, yPosition)

  yPosition += 8

  const fibraHumana = describeFiberIdForUi(trace.fiberId, cablesList).primary
  const clienteHumano = clientNode
    ? [
        (clientNode as { refCode?: string }).refCode,
        clientNode.nombre || (isCliente(clientNode) ? 'Cliente' : ''),
      ]
        .filter(Boolean)
        .join(' · ') || clientNode.id
    : 'N/A'

  const generalInfo: [string, string][] = [
    ['Fibra / enlace:', fibraHumana],
    ['ID técnico fibra:', trace.fiberId.length > 60 ? `${trace.fiberId.slice(0, 40)}…` : trace.fiberId],
    ['Cliente:', clienteHumano],
    ['Tipo de Servicio:', clientNode && isCliente(clientNode) ? (clientNode.servicio?.tipoServicio || 'N/A') : 'N/A'],
    ['Distancia Total:', `${trace.totalDistanceKm.toFixed(2)} km`],
    ['Segmentos:', `${trace.path.length}`],
    ['Empalmes (API):', `${trace.splicingPoints.length}`],
    ['Conectores (API):', `${trace.connectorsCount}`],
  ]

  if (budget) {
    generalInfo.push(
      ['Potencia Tx (SFP):', `${budget.potenciaTx} dBm`],
      ['Sensibilidad Rx (ONT):', `${budget.sensibilidadRx} dBm`],
      ['Margen de diseño:', `${budget.margenDiseno} dB`],
      ['Potencia estimada en ONT:', `${(budget.potenciaTx - budget.perdidaTotal).toFixed(2)} dBm`],
      ['Margen de enlace:', `${budget.margenRestante.toFixed(2)} dB`],
    )
  }

  generalInfo.forEach(([label, value]) => {
    doc.setFontSize(10)
    doc.setTextColor(...grayColor)
    doc.text(label, 20, yPosition)

    doc.setTextColor(30, 41, 59)
    doc.setFont('helvetica', 'bold')
    doc.text(value, 65, yPosition)
    doc.setFont('helvetica', 'normal')

    yPosition += 6
  })

  yPosition += 5

  // ============================================================================
  // ESTADO DEL ENLACE (DESTACADO)
  // ============================================================================

  const bannerH = budget ? 34 : 25
  doc.setFillColor(...statusColor)
  doc.roundedRect(15, yPosition, 180, bannerH, 3, 3, 'F')

  doc.setFontSize(14)
  doc.setTextColor(255, 255, 255)
  doc.text('Estado del Enlace', 20, yPosition + 8)

  doc.setFontSize(budget ? 16 : 20)
  doc.setFont('helvetica', 'bold')
  doc.text(bannerTitle, 20, yPosition + 18)

  doc.setFontSize(14)
  doc.text(`${totalLoss.toFixed(2)} dB`, 150, yPosition + 18)
  doc.setFont('helvetica', 'normal')

  if (budget) {
    doc.setFontSize(8)
    doc.text(
      `Margen: ${budget.margenRestante.toFixed(2)} dB  |  P en ONT: ${(budget.potenciaTx - budget.perdidaTotal).toFixed(2)} dBm`,
      20,
      yPosition + 28
    )
  }

  yPosition += bannerH + 10

  // ============================================================================
  // DESGLOSE DE PÉRDIDAS
  // ============================================================================

  doc.setFontSize(12)
  doc.setTextColor(30, 41, 59)
  doc.text(budget ? 'Desglose de Pérdidas (presupuesto óptico)' : 'Desglose de Pérdidas', 15, yPosition)

  yPosition += 8

  type LossRow = (string | { content: string; styles?: Record<string, unknown> })[]
  const lossData: LossRow[] = budget
    ? [
        [
          'Atenuación de fibra',
          `${budget.longitudTotalKm.toFixed(3)} km`,
          `${budget.detalles.coeficienteFibra} dB/km`,
          `${budget.perdidaFibra.toFixed(2)} dB`,
        ],
        [
          'Empalmes',
          `${budget.totalEmpalmes}`,
          `${budget.detalles.perdidaPorEmpalme} dB/empalme`,
          `${budget.perdidaEmpalmes.toFixed(2)} dB`,
        ],
        [
          'Conectores',
          `${budget.totalConectores}`,
          `${budget.detalles.perdidaPorConector} dB/conector`,
          `${budget.perdidaConectores.toFixed(2)} dB`,
        ],
        ...(budget.perdidaSplitter > 0
          ? ([
              [
                'Splitters (NAP)',
                `${budget.totalSplitters}`,
                'según config. NAP',
                `${budget.perdidaSplitter.toFixed(2)} dB`,
              ],
            ] satisfies LossRow[])
          : []),
        [
          { content: 'TOTAL', styles: { fontStyle: 'bold' } },
          '',
          '',
          {
            content: `${budget.perdidaTotal.toFixed(2)} dB`,
            styles: { fontStyle: 'bold', textColor: statusColor },
          },
        ],
      ]
    : [
        ...[
          {
            component: 'Conectores',
            count: trace.connectorsCount,
            lossPerUnit: 0.5,
            unit: 'conector',
          },
          {
            component: 'Empalmes',
            count: trace.splicingPoints.length,
            lossPerUnit: 0.1,
            unit: 'empalme',
          },
          {
            component: 'Atenuación de Fibra',
            count: trace.totalDistanceKm,
            lossPerUnit: 0.3,
            unit: 'km',
          },
        ].map((item) => {
          const t = Math.round(item.count * item.lossPerUnit * 100) / 100
          return [
            item.component,
            `${item.count} ${item.unit}${item.count !== 1 ? 's' : ''}`,
            `${item.lossPerUnit} dB/${item.unit}`,
            `${t.toFixed(2)} dB`,
          ] satisfies LossRow
        }),
        [
          { content: 'TOTAL', styles: { fontStyle: 'bold' } },
          '',
          '',
          {
            content: `${legacyLoss.toFixed(2)} dB`,
            styles: { fontStyle: 'bold', textColor: statusColor },
          },
        ],
      ]

  autoTable(doc, {
    startY: yPosition,
    head: [['Componente', 'Cantidad', 'Pérdida Unitaria', 'Pérdida Total']],
    body: lossData,
    theme: 'striped',
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontSize: 10,
      fontStyle: 'bold',
    },
    styles: {
      fontSize: 9,
      cellPadding: 4,
    },
    columnStyles: {
      0: { cellWidth: 60 },
      1: { cellWidth: 40 },
      2: { cellWidth: 45 },
      3: { cellWidth: 40, halign: 'right' },
    },
  })

  yPosition = (doc as any).lastAutoTable.finalY + 15

  // ============================================================================
  // RUTA COMPLETA
  // ============================================================================

  // Verificar si necesitamos nueva página
  if (yPosition > 220) {
    doc.addPage()
    yPosition = 20
  }

  doc.setFontSize(12)
  doc.setTextColor(30, 41, 59)
  doc.text('Ruta Completa', 15, yPosition)

  yPosition += 8

  const routeData = trace.path.map((segment, index) => {
    const node = nodeMap.get(segment.nodeId)
    const ref = segment.nodeRefCode || (node as { refCode?: string } | undefined)?.refCode
    const nom = segment.description || (node as { nombre?: string } | undefined)?.nombre
    const nodeName = [ref, nom].filter(Boolean).join(' · ') || segment.nodeId
    const nodeType = node ? node.tipo : '-'

    let cableCol = '—'
    if (segment.cableId) {
      const fromApi = [segment.cableCodigo && `[${segment.cableCodigo}]`, segment.cableNombre].filter(
        Boolean
      ) as string[]
      if (cableMap) {
        const cab = cableMap.get(segment.cableId)
        if (cab) cableCol = cableLabelShort(cab)
        else cableCol = fromApi.length > 0 ? fromApi.join(' ') : `${segment.cableId.slice(0, 10)}…`
      } else {
        cableCol = fromApi.length > 0 ? fromApi.join(' ') : `${segment.cableId.slice(0, 10)}…`
      }
    }

    return [
      `${index + 1}`,
      nodeName,
      nodeType,
      cableCol,
      `${segment.distanceKm.toFixed(2)} km`,
      segment.hasSplice ? 'Sí' : 'No',
    ]
  })

  autoTable(doc, {
    startY: yPosition,
    head: [['#', 'Nodo', 'Tipo', 'Cable (código)', 'Distancia', 'Empalme']],
    body: routeData,
    theme: 'grid',
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: 'bold',
    },
    styles: {
      fontSize: 8,
      cellPadding: 3,
    },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 50 },
      2: { cellWidth: 25, halign: 'center' },
      3: { cellWidth: 40 },
      4: { cellWidth: 30, halign: 'right' },
      5: { cellWidth: 25, halign: 'center' },
    },
    didParseCell: (data) => {
      // Highlight de empalmes
      if (data.column.index === 5 && data.cell.text[0] === 'Sí') {
        data.cell.styles.fillColor = [254, 243, 199] // Amarillo claro
        data.cell.styles.textColor = [217, 119, 6] // Naranja
      }
    },
  })

  yPosition = (doc as any).lastAutoTable.finalY + 15

  // ============================================================================
  // PUNTOS DE EMPALME
  // ============================================================================

  if (trace.splicingPoints.length > 0) {
    if (yPosition > 240) {
      doc.addPage()
      yPosition = 20
    }

    doc.setFontSize(12)
    doc.setTextColor(30, 41, 59)
    doc.text('Puntos de Empalme', 15, yPosition)

    yPosition += 8

    const splicingData = trace.splicingPoints.map((point, index) => {
      const n = nodeMap.get(point)
      const label = [
        (n as { refCode?: string } | undefined)?.refCode,
        (n as { nombre?: string } | undefined)?.nombre,
      ]
        .filter(Boolean)
        .join(' · ') || point
      return [`${index + 1}`, label, '~0.1 dB']
    })

    autoTable(doc, {
      startY: yPosition,
      head: [['#', 'Ubicación (Nodo)', 'Pérdida Estimada']],
      body: splicingData,
      theme: 'plain',
      headStyles: {
        fillColor: [255, 237, 213], // Naranja claro
        textColor: [194, 65, 12], // Naranja oscuro
        fontSize: 9,
        fontStyle: 'bold',
      },
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 15, halign: 'center' },
        1: { cellWidth: 130 },
        2: { cellWidth: 40, halign: 'right' },
      },
    })

    yPosition = (doc as any).lastAutoTable.finalY + 15
  }

  // ============================================================================
  // PIE DE PÁGINA EN TODAS LAS PÁGINAS
  // ============================================================================

  const pageCount = doc.getNumberOfPages()

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)

    // Línea superior
    doc.setDrawColor(...grayColor)
    doc.setLineWidth(0.3)
    doc.line(15, 280, 195, 280)

    // Texto
    doc.setFontSize(8)
    doc.setTextColor(...grayColor)
    doc.text('Generado por NetEasyFTTX - Sistema de Gestión FTTH', 15, 285)
    doc.text(`Página ${i} de ${pageCount}`, 180, 285, { align: 'right' })

    if (showCriticalFooter) {
      doc.setTextColor(...dangerColor)
      doc.setFont('helvetica', 'bold')
      doc.text(
        budget
          ? 'ATENCION: Enlace no viable o margen negativo - Revisar SFP, splitters y ruta'
          : 'ATENCION: Perdida critica detectada - Requiere revision inmediata',
        15,
        290
      )
      doc.setFont('helvetica', 'normal')
    }
  }

  // ============================================================================
  // GUARDAR PDF
  // ============================================================================

  const fileName = `trazabilidad_${trace.fiberId.replace(/[|/\\]/g, '_')}_${Date.now()}.pdf`
  doc.save(fileName)

  return fileName
}

// ============================================================================
// FUNCIONES AUXILIARES
// ============================================================================

function calculateTotalLoss(trace: TraceResult): number {
  let totalLoss = 0
  totalLoss += trace.connectorsCount * 0.5
  totalLoss += trace.splicingPoints.length * 0.1
  totalLoss += trace.totalDistanceKm * 0.3
  return Math.round(totalLoss * 100) / 100
}

function getStatus(loss: number): string {
  if (loss < 15) return 'excelente'
  if (loss < 20) return 'aceptable'
  return 'crítico'
}

function getStatusColor(loss: number): [number, number, number] {
  if (loss < 15) return [16, 185, 129] // Verde
  if (loss < 20) return [234, 179, 8] // Amarillo
  return [239, 68, 68] // Rojo
}
